// client/src/coms/views/games_parts/WatchHostDialog.js
// Starting Watch Together from Activities: a name, who controls playback, and optionally a voice channel of
// this server to show it in (the one you are in, by default). No voice channel is needed: everyone can join
// with the invite code or link.
import React, { useMemo, useState } from 'react';

export default function WatchHostDialog({ server, voiceSession, busy = false, error = '', onCancel, onCreate }) {
  const channels = useMemo(
    () => (Array.isArray(server && server.channels) ? server.channels : []).filter((c) => c && c.kind === 'voice'),
    [server],
  );
  const inThisServer = voiceSession && voiceSession.status === 'connected' && server && String(voiceSession.serverId) === String(server.id);
  const [channelId, setChannelId] = useState(() => (inThisServer && voiceSession.channelDbId ? String(voiceSession.channelDbId) : ''));
  const [title, setTitle] = useState('');
  const [controlMode, setControlMode] = useState('host');
  const [audience, setAudience] = useState('channel');
  const chosen = channels.find((c) => String(c.db_id != null ? c.db_id : c.id) === channelId);

  return (
    <div className="party-modal-backdrop" role="dialog" aria-modal="true" aria-label="Start Watch Together" onClick={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <div className="party-modal" style={{ '--party-accent': '#e53935' }}>
        <div className="party-modal-head">
          <span className="party-card-icon" aria-hidden="true">📺</span>
          <div>
            <div className="party-kicker">Start an activity · voice optional</div>
            <h3>Watch Together</h3>
          </div>
        </div>
        <label className="party-setting">
          <span>Session name</span>
          <input value={title} maxLength={80} placeholder="Watch Together" onChange={(event) => setTitle(event.target.value)} data-testid="watch-title-input" />
        </label>
        <label className="party-setting">
          <span>Playback control</span>
          <select value={controlMode} onChange={(event) => setControlMode(event.target.value)} data-testid="watch-control-choice">
            <option value="host">Only I control playback (you can hand over control later)</option>
            <option value="everyone">Collaborative: everyone can play, pause and add videos</option>
          </select>
        </label>
        {channels.length ? (
          <label className="party-setting">
            <span>Voice channel (optional)</span>
            <select value={channelId} onChange={(event) => setChannelId(event.target.value)} data-testid="watch-channel">
              <option value="">No voice channel</option>
              {channels.map((c) => {
                const id = String(c.db_id != null ? c.db_id : c.id);
                return <option key={id} value={id}>🔊 {c.name}{inThisServer && String(voiceSession.channelDbId) === id ? ' (you are here)' : ''}</option>;
              })}
            </select>
          </label>
        ) : null}
        {chosen ? (
          <label className="party-setting">
            <span>Who can find it</span>
            <select value={audience} onChange={(event) => setAudience(event.target.value)} data-testid="watch-audience-choice">
              <option value="channel">People who can join #{chosen.name}, and anyone with the code</option>
              <option value="invite">Only people I give the code to</option>
            </select>
          </label>
        ) : null}
        <p className="party-muted">
          You get an invite code and link to share. People join without entering a voice channel.
          {chosen ? ` The session also shows in #${chosen.name}, so people there can join with one click.` : ''}
        </p>
        {error ? <div role="alert" className="party-error">{error}</div> : null}
        <div className="party-row end">
          <button type="button" className="party-btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="party-btn"
            data-testid="watch-create"
            disabled={busy}
            onClick={() => onCreate({ title: title.trim(), controlMode, channelId: chosen ? Number(channelId) : 0, audience })}
          >
            {busy ? 'Starting…' : 'Start session'}
          </button>
        </div>
      </div>
    </div>
  );
}
